import { parseIsoDate } from "@/features/events/eventDate";
import type { Resource } from "@/api/types/booking";

/**
 * Client-side booking-price math — PREVIEW ONLY, mirrors
 * app/services/booking_pricing.py's weekday/weekend + 25%-extra-hour
 * formula exactly. Same house rule as features/quotes/quoteMath.ts: the
 * server is the source of truth for every cent actually stored
 * (Booking.price_cents is always the server's own computation) — this
 * exists purely to render a live "here's what this will cost" preview in
 * the create-booking form before submit. After a successful POST, the UI
 * must display the server-returned `price_cents`, not this preview.
 */

/** Weekend = Saturday + Sunday (business rule). `Date.getDay()`: 0=Sun, 6=Sat. */
export function isWeekendIso(iso: string): boolean {
  const day = parseIsoDate(iso).getDay();
  return day === 0 || day === 6;
}

export function basePriceCentsPreview(resource: Pick<Resource, "weekday_price_cents" | "weekend_price_cents">, iso: string): number {
  return isWeekendIso(iso) ? resource.weekend_price_cents : resource.weekday_price_cents;
}

/** 25% of the block price, rounded to the nearest cent (round-half-up,
 * matching app/services/quote_math.py::pct_of_cents — JS `Math.round`
 * already rounds .5 up for positive numbers, so no extra care is needed
 * for the whole-cent inputs this always receives). */
export function extraHourAddonCentsPreview(baseCents: number): number {
  return Math.round(baseCents * 0.25);
}

export function totalPriceCentsPreview(
  resource: Pick<Resource, "weekday_price_cents" | "weekend_price_cents">,
  iso: string,
  extraHour: boolean,
): number {
  const base = basePriceCentsPreview(resource, iso);
  return extraHour ? base + extraHourAddonCentsPreview(base) : base;
}
